import React, { Fragment, Component } from 'react'
import PropTypes from 'prop-types'
import { Link, Switch } from 'react-router-dom'
import classNames from 'classnames'
import { Container, Logo, PreHeader } from '@scentregroup/ui'
import qs from 'query-string'
import pagesHoc from '../pages-hoc'
import NationalFooter from '../../components/national-footer'
import Route from '../../components/boundary-route'
import containerStyle from '../../components/container.scss'
import withProductCategories from '../../containers/with-product-categories'
import Header from './header'
import Home from './home'
import ProductDetail from './product-detail'
import Products from './products'
import style from './index.scss'

function getCategorySlugFromPath(match) {
  return (
    match.params.subCategory2 ||
    match.params.subCategory1 ||
    match.params.category ||
    'products'
  )
}

function trimSlashes(url) {
  const matches = url.match(/^\/(\S+[^/])\/?$/)
  return matches.length > 1 ? matches[1] : url
}

function getFilterStateFromPath({ location }) {
  const queryParams = qs.parse(location.search)
  const page = Math.max(0, (queryParams.page || 1) - 1)
  delete queryParams.page
  const onSale = queryParams.on_sale === 'true'
  delete queryParams.on_sale

  const filterState = {
    onSale,
    page,
    ...queryParams,
  }
  if (queryParams.retailer) {
    filterState.store = queryParams.retailer
  }
  if (queryParams.search_query) {
    filterState.searchQuery = queryParams.search_query
  }
  if (queryParams.colour) {
    filterState.color = queryParams.colour
  }
  return filterState
}

export function buildUrlFromFilterState(filterState) {
  const params = {}
  const queryParams = [
    'brand',
    'page',
    'sort',
    'minPrice',
    'maxPrice',
    'search_query',
  ]

  // resolve naming diff's
  params['retailer'] = filterState['store']
  params['on_sale'] = filterState['onSale']
  params['colour'] = filterState['color']

  queryParams.forEach(param => (params[param] = filterState[param]))
  // Update the page param if it exists
  if (params.page !== undefined) {
    params.page++
    if (params.page === 1) {
      delete params.page
    }
  }
  // Remove the onSale param if it's set to false
  if (!params.on_sale) {
    delete params.on_sale
  }

  const prefix =
    filterState.categoryPath === 'products'
      ? filterState.prefix || 'products'
      : filterState.categoryPath

  const paramsOrder = ['colour', 'retailer', 'brand', 'on_sale', 'page']
  const search = qs.stringify(params, {
    sort: (m, n) => paramsOrder.indexOf(m) >= paramsOrder.indexOf(n),
  })
  return `/${prefix}?${search}`
}

class CategoriesPage extends Component {
  constructor(props) {
    super(props)
    const queryParams = qs.parse(props.location.search)
    this.state = {
      navigationMenu: this.navigationMenu(props.productCategories),
      searchQuery: queryParams.search_query,
    }
  }

  navigationMenu(categories) {
    const topCategories = categories.filter(category => category.depth === 1)

    return topCategories.map(topCategory => ({
      title: topCategory.name,
      url: `/${topCategory.path}`,
      items: categories
        .filter(category => category.parent_id === topCategory.category_id)
        .map(category => ({
          url: `/${category.path}`,
          label: category.name,
        }))
        .concat([
          {
            url: `/${topCategory.path}?on_sale=true`,
            label: 'Sale Items',
          },
        ]),
    }))
  }

  render() {
    return (
      <Fragment>
        <PreHeader colour="brand">
          <Link to="/" title="Home" className={style.preHeaderLogo}>
            <Logo />
          </Link>
        </PreHeader>
        <Header
          navigationMenu={this.state.navigationMenu}
          history={this.props.history}
          searchQuery={this.state.searchQuery}
        />
        <Container
          className={classNames(
            containerStyle.container,
            containerStyle.containerSpaced,
            style.productsContainer
          )}
          type="full"
        >
          <Switch>
            <Route
              path={'/products/:type(retailer|brand)/:slug'}
              render={routeProps => {
                const filterParam =
                  routeProps.match.params.type === 'retailer'
                    ? 'store'
                    : 'brand'
                return (
                  <Products
                    hideCategoryInformation
                    filterState={{
                      categorySlug: 'products',
                      categoryPath: 'products',
                      [filterParam]: routeProps.match.params.slug,
                      prefix: routeProps.match.url.substring(1), // Remove the leading '/'
                      ...getFilterStateFromPath(routeProps),
                    }}
                    buildUrl={buildUrlFromFilterState}
                    categorySlug="products"
                    subcategoryDepth={2}
                    {...routeProps}
                  />
                )
              }}
            />
            <Route
              strict
              path={`/products/:retailer/:title/:id(\\w{8}-\\w{4}-\\w{4}-\\w{4}-\\w{12})`}
              render={routeProps => <ProductDetail {...routeProps} />}
            />
            <Route
              path={`/products/:category/:subCategory1?/:subCategory2?`}
              render={routeProps => (
                <Products
                  brand
                  filterState={{
                    categorySlug: getCategorySlugFromPath(routeProps.match),
                    categoryPath: trimSlashes(routeProps.match.url),
                    ...getFilterStateFromPath(routeProps),
                  }}
                  buildUrl={filterState => buildUrlFromFilterState(filterState)}
                  categorySlug={getCategorySlugFromPath(routeProps.match)}
                  subcategoryDepth={1}
                  {...routeProps}
                />
              )}
            />
            <Route
              path="/products"
              render={routeProps => {
                const queryParams = qs.parse(routeProps.location.search)
                if (queryParams.search_query) {
                  return (
                    <Products
                      brand
                      filterState={{
                        categorySlug: getCategorySlugFromPath(routeProps.match),
                        categoryPath: routeProps.match.url.substring(1),
                        ...getFilterStateFromPath(routeProps),
                      }}
                      buildUrl={filterState =>
                        buildUrlFromFilterState(filterState)
                      }
                      categorySlug={getCategorySlugFromPath(routeProps.match)}
                      subcategoryDepth={2}
                      {...routeProps}
                    />
                  )
                } else {
                  return <Home {...routeProps} />
                }
              }}
            />
          </Switch>
        </Container>
        <NationalFooter colour="brand" />
      </Fragment>
    )
  }
}

CategoriesPage.propTypes = {
  productCategories: PropTypes.array,
  history: PropTypes.object,
  location: PropTypes.object,
}

const getMetaData = () => ({
  title: 'Products at Westfield',
  description: '',
})

export default withProductCategories(pagesHoc(CategoriesPage, getMetaData))
